import { observer } from 'mobx-react-lite';

import DevGameImage from '#/dev-game.png';
import { FilteredGameList, gameTagFilter } from '@/common/components/FilteredGameList';
import { BaseAppModel } from '@/common/models/AppModel';
import { GameInfo } from '@/types';
import { ClassNames } from '@emotion/react';
import {
  font,
  fontFamilies,
  fontSizes,
  standardSizes,
  TabData,
  Tabs,
} from '@toilet-paper-games/ui';
import { DEV_GAME_ID } from '../constants';
import { makeSimpleAutoObservable } from '../utils/mobx/mobx';
import { BaseViewModel, useViewModelConstructor } from '../utils/mobx/ViewModel';

export interface GameIconInfo extends Pick<
  GameInfo,
  'uniqueName' | 'displayName' | 'image' | 'tags'
> {}

export interface GameListViewModelProps<T extends BaseAppModel> {
  appModel: T;
}

export class GameListViewModel<T extends BaseAppModel> extends BaseViewModel<
  GameListViewModelProps<T>
> {
  constructor(props: GameListViewModelProps<T>) {
    super(props);

    makeSimpleAutoObservable(this, {}, { autoBind: true });
  }

  get appModel() {
    return this.props.appModel;
  }

  get gameIconInfoList(): GameIconInfo[] {
    const games = Array.from(this.appModel.games.values()).filter(
      (game) =>
        this.appModel.showHiddenGames ||
        !game.hideFromList ||
        game.uniqueName === DEV_GAME_ID,
    );
    if (!this.appModel.devMode) return games;
    this.appModel.devGameUrl;

    const devGame: GameIconInfo = {
      uniqueName: 'dev',
      displayName: 'Dev Game',
      image: DevGameImage,
      tags: ['dev'],
    };

    return [devGame, ...games];
  }

  selectGame(gameUniqueName: string) {
    this.appModel.trySelectGame(gameUniqueName);
  }
}

interface GameListProps<T extends BaseAppModel> {
  tabSelectorAlignment: 'left' | 'center' | 'right';
  tabsAtBottom?: boolean;
  plainTabBody?: boolean;
  appModel: T;
  onGameSelected?: (gameUniqueName: string) => void;
}

export const GameList = observer((props: GameListProps<BaseAppModel>) => {
  const { appModel, plainTabBody, tabsAtBottom, tabSelectorAlignment } = props;
  const viewModel = useViewModelConstructor(GameListViewModel, {
    appModel,
  });

  const displays: TabData[] = [
    {
      id: 'All',
      title: 'All',
      display: () => (
        <FilteredGameList
          plain={plainTabBody}
          viewModel={viewModel}
          onGameSelected={props.onGameSelected}
        />
      ),
    },
    {
      id: '2D',
      title: '2D',
      display: () => (
        <FilteredGameList
          filter={gameTagFilter(['2d'])}
          plain={plainTabBody}
          viewModel={viewModel}
          onGameSelected={props.onGameSelected}
        />
      ),
    },
    {
      id: 'Words',
      title: 'Word Games',
      display: () => (
        <FilteredGameList
          filter={gameTagFilter(['words'])}
          plain={plainTabBody}
          viewModel={viewModel}
          onGameSelected={props.onGameSelected}
        />
      ),
    },
    {
      id: 'Realtime',
      title: 'Realtime',
      display: () => (
        <FilteredGameList
          filter={gameTagFilter(['realtime'])}
          plain={plainTabBody}
          viewModel={viewModel}
          onGameSelected={props.onGameSelected}
        />
      ),
    },
  ];

  return (
    <ClassNames>
      {({ css }) => (
        <Tabs
          css={[
            font(fontFamilies.primary, fontSizes.md),
            tabsAtBottom && { flexDirection: 'column-reverse !important' },
          ]}
          tabs={displays}
          tabSelectorAlignment={tabSelectorAlignment}
          tabBodyClassName={css({ div: { borderRadius: standardSizes.xl } })}
        />
      )}
    </ClassNames>
  );
});
